<template>
    <div>
        <report-header
            :title="title"
            :isLoading="isLoading"
            :reportPagination="pagination"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />
        <b-card id="report-card">
            <div id="report-body" class="w-100">
                <div class="header pt-3">
                    <div class="col-6 p-0"></div>
                    <div class="col-6 p-0 search-option">
                        <p style="color: #ffffff;">.</p>
                        <input
                            v-if="!isLoading"
                            v-model="filterPatient"
                            class="form-control col-md-6"
                            name="title"
                            placeholder="Search by name"
                        />
                        <i
                            class="fas fa-times"
                            v-if="filterPatient"
                            @click="filterPatient = ''"
                        ></i>
                    </div>
                </div>
                <QTable
                    class="sticky-table"
                    style="overscroll-behavior: contain;"
                    :data="result"
                    :columns="formattedColumns"
                    :hide-bottom="true"
                    :rows-per-page-options="[0]"
                    :visible-columns="visibleColumns"
                    virtual-scroll
                    row-key="patient_id"
                    :filter="filterPatient"
                    :filter-method="filteringResults"
                >
                    <template v-slot:header="props">
                        <q-tr :props="props">
                            <q-th
                                v-for="col in props.cols"
                                :key="col.name"
                                :props="props"
                                class="sticky-header"
                            >
                                <strong>{{ col.label }}</strong>
                            </q-th>
                        </q-tr>
                        <q-linear-progress
                            v-show="isLoading"
                            class="sticky-loader"
                            indeterminate
                            color="dark"
                        />
                    </template>
                    <template
                        v-slot:bottom-row
                        v-if="result.length == 0 && !isLoading && !isError"
                    >
                        <q-tr>
                            <q-td colspan="12">No records available</q-td>
                        </q-tr>
                    </template>
                    <template v-slot:body-cell-primary_owner="props">
                        <q-td :props="props" class="">
                            <client-name
                                :use-router="USE_CLIENT_MODULE_VUE"
                                :client="generateClientObject(props.row)"
                            />
                        </q-td>
                    </template>
                    <template v-slot:body-cell-name="props">
                        <q-td :props="props" class="">
                            <patient-name
                                :use-router="USE_PATIENT_MODULE_VUE"
                                :patient="generatePatientObject(props.row)"
                                :show-status="true"
                            />
                        </q-td>
                    </template>
                    <template v-slot:body-cell-homeLocation="props">
                        <q-td :props="props" class="">
                            <span v-if="props.row.primary_owner_id">
                                {{ props.row.homeLocation }}
                            </span>
                        </q-td>
                    </template>
                </QTable>
            </div>
        </b-card>
    </div>
</template>
<script>
import Report from "@/mixins/report.js";
import "@/css/quasar-table.css";
import { QTable } from "quasar";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import ClientName from "@/components/commons/client-name";
import PatientName from "@/components/commons/patient-name";
export default {
    components: {
        QTable,
        ClientName,
        PatientName,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("month"),
                    end: this.getEndDate("day")
                };
            }
        }
    },
    data() {
        return {
            dateRange: "",
            isLoading: true,
            filterPatient: null,
            result: [],
            title: "Deceased Patient List",
            isError: false,
            isDestroyed: false,
            isRecordSharing: false,
            formattedColumns: [
                {
                    name: "name",
                    label: "Name",
                    field: "name",
                    align: "left",
                    style: "min-width: 150px;width:10%"
                },
                {
                    name: "species",
                    label: "Species",
                    field: "species",
                    align: "left",
                    style: "min-width: 110px;width: 10%"
                },
                {
                    name: "breed",
                    label: "Breed",
                    field: "breed",
                    align: "left",
                    style: "min-width: 110px;width: 10%"
                },
                {
                    name: "sex",
                    label: "Sex",
                    field: "sex",
                    align: "left",
                    style: "min-width: 110px;width: 10%"
                },
                {
                    name: "age",
                    label: "Age",
                    field: "age",
                    align: "left",
                    style: "min-width: 110px;width: 10%",
                    classes: "text-capitalize"
                },
                {
                    name: "birthdate",
                    label: "Birthdate",
                    field: "birthdate",
                    align: "left",
                    style: "min-width: 110px;width: 8%"
                },
                {
                    name: "color",
                    label: "Color",
                    field: "color",
                    align: "left",
                    style: "min-width: 110px;width: 8%",
                    classes: "text-capitalize"
                },
                {
                    name: "chip",
                    label: "Chip",
                    field: "chip",
                    align: "left",
                    style: "min-width: 110px;width: 8%",
                    classes: "text-capitalize"
                },
                {
                    name: "primary_owner",
                    label: "Primary Owner",
                    field: "primary_owner",
                    align: "left",
                    style: "min-width: 150px;width: 10%"
                },
                {
                    name: "status_date",
                    label: "Date",
                    field: "status_date",
                    format: value => {
                        return this._toDateTime(parseFloat(value)).toFormat(
                            this._getLuxonFormat()
                        );
                    },
                    align: "left",
                    style: "min-width: 80px;width: 8%",
                    classes: "text-capitalize"
                },
                {
                    name: "note",
                    label: "Notes",
                    field: "note",
                    align: "left",
                    style: "min-width: 80px;width: 8%",
                    classes: "text-capitalize"
                },
                {
                    name: "homeLocation",
                    label: "Home Location",
                    field: "homeLocation",
                    align: "left",
                    style: "min-width: 150px;width: 8%",
                    classes: "text-capitalize"
                }
            ]
        };
    },
    computed: {
        visibleColumns() {
            let self = this;
            let cols = self.formattedColumns.map(e => e.field);
            const index = cols.findIndex(e => e == "homeLocation");

            if (!self.isRecordSharing) cols.splice(index, 1);
            return cols;
        }
    },
    async mounted() {
        let self = this;
        await self._getSetting();
        self.data.isShowDateRange = true;
        self.data.datePickerOptionDefault = 3;
        self.data.start = self.getStartDate("month");
        self.data.end = self.getEndDate("day");
        self.data.from = self.converToUnix(self.data.start);
        self.data.to = self.converToUnix(self.data.end);

        self.data.exportOption = false;
        self.init();
        self.$root.$on("report-filter-date-range:refresh", self.prepareData);
    },
    beforeDestroy: function () {
        this.data.isDestroyed = true;
        this.data.exportOptionCsvOnly = false;
        this.data.exportOption = true;
        this.resetPagination();
        this.$root.$off("report-filter-date-range:refresh", this.prepareData);
    },
    methods: {
        prepareData(params) {
            this.result = [];
            this.isLoading = true;
            this.resetPagination();
            if (params) {
                this.data.from = params.from;
                this.data.to = params.to;
                this.data.start = params.start;
                this.data.end = params.end;
            }
            this.init();
        },
        async init() {
            let self = this;
            self.toggleOptions(false);
            self.isDestroyed = false;
            self.isLoading = true;
            self.isError = false;
            let params = {
                page: self.pagination.page,
                perPage: self.RECORD_PER_PAGE,
                from: self.data.from,
                to: self.data.to
            };

            clearInterval(self.pagination.interval);
            self.dateRange = self.getDateRange(self.data.from, self.data.to);
            await self
                .fetchRecords(params)
                .then(res => {
                    if (res.message.code === 200) {
                        self.dateRange = self.getDateRange(
                            res.resource.results.from,
                            res.resource.results.to
                        );
                        self.result = res.resource.results.data;
                        self.pagination.lastPage = Math.ceil(
                            res.resource.meta.item_count / self.RECORD_PER_PAGE
                        );
                        self.continueFetch(params);
                        self.dynamicPageTitle(self.title);
                        self.isRecordSharing = res.resource.isRecordSharing;
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    console.error(e);
                    self.isError = true;
                    self.isLoading = false;
                    self.loadingCompleted();
                    self._message("Error: Unable to get the report", "danger");
                });
        },
        async continueFetch(params) {
            let self = this;

            while (
                self.pagination.page < self.pagination.lastPage &&
                !self.isDestroyed &&
                !self.isError
            ) {
                self.pagination.page += 1;
                params.page = self.pagination.page;
                await self
                    .fetchRecords(params)
                    .then(res => {
                        if (res.message.code === 200) {
                            self.result = self.result.concat(
                                res.resource.results.data
                            );
                        } else {
                            throw new Error(
                                "Error: Unable to continue get the report"
                            );
                        }
                    })
                    .catch(e => {
                        console.error(e);
                        self.isError = true;
                        self.isLoading = false;
                        self.result = [];
                        self.loadingCompleted();
                        self._message(
                            "Error: Unable to continue get the report",
                            "danger"
                        );
                    });
            }
            self.loadingCompleted();
            setTimeout(() => {
                self.toggleOptions(true);
                self.isLoading = false;
            }, 1000);
        },
        fetchRecords(params) {
            this.loadingPercentages();
            return ReportService.getReport(
                CREPORT.TYPE.DECEASED_PATIENT_LIST,
                params
            );
        },
        generateClientObject(row) {
            const isDeleted = this.capitalize(row.primary_owner).includes(
                "(deleted)"
            );
            return {
                id: isDeleted ? null : row.primary_owner_id,
                name: this.capitalize(
                    row.primary_owner.replace("(deleted)", "")
                ),
                encoded_id: isDeleted ? null : row.primaryOwner_encoded_id,
                isactive: !isDeleted
            };
        },
        generatePatientObject(row) {
            return {
                name: this.capitalize(row.patient_name),
                encodedId: row.patient_encoded_id,
                isactive: true
            };
        },
        toggleOptions(value) {
            // to hide the action menu when data is loading
            this.data.filterDateOption = value;
            this.data.exportOptionCsvOnly = value;
            this.data.refreshOption = value;
        },

        filteringResults(rows) {
            let self = this;
            return rows.filter(row => {
                return row.patient_name
                    ? row.patient_name
                          .toLowerCase()
                          .includes(self.filterPatient.toLowerCase())
                    : false;
            });
        }
    }
};
</script>
